import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

export const updateUserLesson = async (userId, lessonId, progressData) => {
  try {
    const docId = `${userId}_${lessonId}`;
    const docRef = doc(db, 'user_lessons', docId);
    const lessonDoc = {
      userId,
      lessonId,
      lastUpdated: serverTimestamp(),
      ...progressData
    };
    
    await setDoc(docRef, lessonDoc, { merge: true });
    
    // Trigger computed collection updates if lesson was completed
    if (progressData.status === 'completed') {
      await triggerComputedUpdates(userId, lessonId);
    }
    
    return lessonDoc;
  } catch (error) {
    console.error('Error updating user lesson:', error);
    throw error;
  }
};

/**
 * Merge video playback position onto user_lessons without changing
 * status/completed or recomputing user_levels / user_courses.
 * lastUpdated is left untouched so it still reflects learning progress.
 */
export const updateLessonVideoPosition = async (userId, lessonId, { seconds, duration } = {}) => {
  if (!userId || !lessonId) return null;
  try {
    const docId = `${userId}_${lessonId}`;
    const docRef = doc(db, 'user_lessons', docId);
    const payload = {
      userId,
      lessonId,
      videoPositionSeconds: Number(seconds) || 0,
      videoPositionUpdatedAt: new Date().toISOString()
    };
    if (typeof duration === 'number' && duration > 0) {
      payload.videoDurationSeconds = duration;
    }
    await setDoc(docRef, payload, { merge: true });
    return payload;
  } catch (error) {
    console.error('Error updating lesson video position:', error);
    throw error;
  }
};

export const updateUserPrompt = async (userId, promptId, progressData) => {
  try {
    const docRef = doc(db, 'user_prompts', `${userId}_${promptId}`);
    const promptDoc = {
      userId,
      promptId,
      lastUpdated: serverTimestamp(),
      ...progressData
    };
    await setDoc(docRef, promptDoc, { merge: true });
    return promptDoc;
  } catch (error) {
    console.error('Error updating user prompt:', error);
    throw error;
  }
};

export const getUserLessons = async (userId) => {
  try {
    const lessonsRef = collection(db, 'user_lessons');
    const userLessonsQuery = query(lessonsRef, where('userId', '==', userId));
    const snapshot = await getDocs(userLessonsQuery);
    
    const lessons = {};
    snapshot.forEach(doc => {
      lessons[doc.data().lessonId] = doc.data();
    });
    
    return lessons;
  } catch (error) {
    console.error('Error getting user lessons:', error);
    return {};
  }
};

export const getUserPrompts = async (userId) => {
  try {
    const promptsRef = collection(db, 'user_prompts');
    const userPromptsQuery = query(promptsRef, where('userId', '==', userId));
    const snapshot = await getDocs(userPromptsQuery);
    
    const prompts = {};
    snapshot.forEach(doc => {
      prompts[doc.data().promptId] = doc.data();
    });
    
    return prompts;
  } catch (error) {
    console.error('Error getting user prompts:', error);
    return {};
  }
};

export const userHasProgress = async (userId) => {
  try {
    const lessonsRef = collection(db, 'user_lessons');
    const userLessonsQuery = query(lessonsRef, where('userId', '==', userId));
    const snapshot = await getDocs(userLessonsQuery);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking user progress:', error);
    return false;
  }
};

export const getUserLesson = async (userId, lessonId) => {
  try {
    const docRef = doc(db, 'user_lessons', `${userId}_${lessonId}`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Error getting user lesson:', error);
    return null;
  }
};

export const getUserPrompt = async (userId, promptId) => {
  try {
    const docRef = doc(db, 'user_prompts', `${userId}_${promptId}`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Error getting user prompt:', error);
    return null;
  }
};

// Computed collections functions
export const computeUserLevel = async (userId, courseId, levelNumber) => {
  try {
    const lessonsRef = collection(db, 'lessons');
    const levelLessonsQuery = query(
      lessonsRef, 
      where('course_id', '==', courseId),
      where('level', '==', levelNumber)
    );
    const lessonsSnapshot = await getDocs(levelLessonsQuery);
    const levelLessons = lessonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const lessonsProgress = await Promise.all(
      levelLessons.map(lesson => getUserLesson(userId, lesson.id))
    );
    
    const completedLessons = lessonsProgress.filter(progress => progress?.status === 'completed');
    const totalLessons = levelLessons.length;
    const progress = totalLessons > 0 ? completedLessons.length / totalLessons : 0;
    const completed = completedLessons.length === totalLessons;
    
    let currentLesson = null;
    for (const lesson of levelLessons.sort((a, b) => a.lesson_number - b.lesson_number)) {
      const lessonProgress = lessonsProgress.find(p => p?.lessonId === lesson.id);
      if (!lessonProgress || lessonProgress.status !== 'completed') {
        currentLesson = lesson.id;
        break;
      }
    }
    
    const isActive = completedLessons.length > 0 && !completed;
    
    const levelData = {
      userId,
      courseId,
      levelNumber,
      totalLessons,
      completedLessons: completedLessons.length,
      progress,
      completed,
      isActive,
      currentLesson,
      lastUpdated: serverTimestamp()
    };
    
    const levelDocRef = doc(db, 'user_levels', `${userId}_${courseId}_level${levelNumber}`);
    await setDoc(levelDocRef, levelData, { merge: true });
    
    return levelData;
  } catch (error) {
    console.error('Error computing user level:', error);
    throw error;
  }
};

export const getUserLevel = async (userId, courseId, levelNumber) => {
  try {
    const levelDocRef = doc(db, 'user_levels', `${userId}_${courseId}_level${levelNumber}`);
    const docSnap = await getDoc(levelDocRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return await computeUserLevel(userId, courseId, levelNumber);
    }
  } catch (error) {
    console.error('Error getting user level:', error);
    return null;
  }
};

export const getUserLevelsForCourse = async (userId, courseId) => {
  try {
    const levelsRef = collection(db, 'user_levels');
    const userLevelsQuery = query(
      levelsRef,
      where('userId', '==', userId),
      where('courseId', '==', courseId)
    );
    const snapshot = await getDocs(userLevelsQuery);
    
    const levels = {};
    snapshot.forEach(doc => {
      const data = doc.data();
      levels[data.levelNumber] = data;
    });
    
    return levels;
  } catch (error) {
    console.error('Error getting user levels for course:', error);
    return {};
  }
};

export const computeUserCourse = async (userId, courseId) => {
  try {
    const levelsRef = collection(db, 'levels');
    const courseLevelsQuery = query(levelsRef, where('course_id', '==', courseId));
    const levelsSnapshot = await getDocs(courseLevelsQuery);
    const courseLevels = levelsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const levelsProgress = await Promise.all(
      courseLevels.map(level => getUserLevel(userId, courseId, level.level_number))
    );
    
    const completedLevels = levelsProgress.filter(level => level?.completed === true);
    const totalLevels = courseLevels.length;
    const progress = totalLevels > 0 ? completedLevels.length / totalLevels : 0;
    const completed = completedLevels.length === totalLevels;
    
    let currentLevel = null;
    for (const level of courseLevels.sort((a, b) => a.level_number - b.level_number)) {
      const levelProgress = levelsProgress.find(p => p?.levelNumber === level.level_number);
      if (!levelProgress?.completed) {
        currentLevel = level.level_number;
        break;
      }
    }
    
    const isActive = completedLevels.length > 0 && !completed;
    
    const courseData = {
      userId,
      courseId,
      totalLevels,
      completedLevels: completedLevels.length,
      progress,
      completed,
      isActive,
      currentLevel,
      lastUpdated: serverTimestamp(),
      lastAccessed: serverTimestamp()
    };
    
    const courseDocRef = doc(db, 'user_courses', `${userId}_${courseId}`);
    await setDoc(courseDocRef, courseData, { merge: true });
    
    return courseData;
  } catch (error) {
    console.error('Error computing user course:', error);
    throw error;
  }
};

export const getUserCourse = async (userId, courseId) => {
  try {
    const courseDocRef = doc(db, 'user_courses', `${userId}_${courseId}`);
    const docSnap = await getDoc(courseDocRef);
    
    // Always update lastAccessed when course is accessed (fire and forget)
    setDoc(courseDocRef, { lastAccessed: serverTimestamp() }, { merge: true }).catch(() => {
      // Silently fail - don't block if write fails
    });
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return await computeUserCourse(userId, courseId);
    }
  } catch (error) {
    console.error('Error getting user course:', error);
    return null;
  }
};

export const getMostRecentCourse = async (userId) => {
  try {
    const coursesRef = collection(db, 'user_courses');
    const userCoursesQuery = query(
      coursesRef, 
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(userCoursesQuery);
    
    if (snapshot.empty) {
      return null;
    }
    
    // Filter and sort by lastAccessed manually (in case index doesn't exist yet)
    const courses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const coursesWithAccess = courses.filter(c => c.lastAccessed);
    
    if (coursesWithAccess.length === 0) {
      // If no courses have lastAccessed, return the first one (fallback)
      return courses[0];
    }
    
    // Sort by lastAccessed descending
    coursesWithAccess.sort((a, b) => {
      const aTime = a.lastAccessed?.toMillis?.() || 0;
      const bTime = b.lastAccessed?.toMillis?.() || 0;
      return bTime - aTime;
    });
    
    return coursesWithAccess[0];
  } catch (error) {
    console.error('Error getting most recent course:', error);
    return null;
  }
};

export const getUserCourses = async (userId) => {
  try {
    const coursesRef = collection(db, 'user_courses');
    const userCoursesQuery = query(coursesRef, where('userId', '==', userId));
    const snapshot = await getDocs(userCoursesQuery);
    
    const courses = {};
    snapshot.forEach(doc => {
      const data = doc.data();
      courses[data.courseId] = data;
    });
    
    return courses;
  } catch (error) {
    console.error('Error getting user courses:', error);
    return {};
  }
};

const triggerComputedUpdates = async (userId, lessonId) => {
  try {
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonSnap = await getDoc(lessonRef);
    
    if (lessonSnap.exists()) {
      const lessonData = lessonSnap.data();
      const { course_id: courseId, level } = lessonData;
      
      await computeUserLevel(userId, courseId, level);
      await computeUserCourse(userId, courseId);
    }
  } catch (error) {
    console.error('Error triggering computed updates:', error);
  }
};
